import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  type EditorV2State,
  type FireflyTertiaryButtonMenuItem,
  ChakraFlex,
  ChakraFormControl,
  ChakraFormErrorMessage,
  EditorV2,
} from '@equip.health/ui';
import { Form, useFormikContext } from 'formik';
import { get, isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import type { Survey as SurveyJS } from 'survey-react-ui';

import util from '~/components/common/calendar/util';
import { fillActivityWith } from '~/components/maud/patients/note/editNoteForm.util';
import { EditNoteFormAttendeeList } from '~/components/maud/patients/note/EditNoteFormAttendeeList';
import { EditNoteFormAttributesRow } from '~/components/maud/patients/note/EditNoteFormAttributesRow';
import { EditNoteFormTitleRow } from '~/components/maud/patients/note/EditNoteFormTitleRow';
import { useLatestNoteTemplateRevisionsWithLegacyHandling } from '~/components/maud/patients/note/useLatestNoteTemplateRevisionsWithLegacyHandling';
import { type AttachmentType } from '~/lib/constants/AddPatientNoteModal.constants';
import { AppointmentStatus } from '~/lib/constants/calendar.constants';
import { NO_NOTE_TEMPLATE_OPTION } from '~/lib/constants/notes';
import type { EditNoteFormData } from '~/lib/types/notes/EditNoteFormData';
import { getGroupClassActivityWith } from '~/lib/util/classes/groupClasses.util';
import { useGetAdminNoteActivityWithNamesEnumQuery } from '~/store/services/enumerations';
import { useGetAppointmentDetailsByIdQuery } from '~/store/services/notes/notes';
import NoteTemplateRevisionForm from './NoteTemplateRevisionForm';
import NoteTemplateSwitchModal from './NoteTemplateSwitchModal';
import { useGetAppointmentDetailsQuery } from '~/store/services/appointment';
import { isBaseQueryError } from '~/lib/types/api/isQueryError';
import { useNoteFormSessionListOptions } from '~/lib/hooks/useNoteFormSessionListOptions';
import NoteAttachmentCard from './NoteAttachmentCard';

export type FieldErrorState = {
  titleError: string;
  typeError: string;
  editorError: string;
  dateError: string;
  summaryError: string;
  carePlanError: string;
};

export interface EditNoteFormProps {
  noteTemplateRevisionFormRef: React.RefObject<SurveyJS>;
  disableField: boolean; // TODO rename to plural?
  fieldErrorReducer: FieldErrorState;
  hasEventEnded: boolean;
  isAddendumModal?: boolean;
  isEditAttendeesClicked?: boolean;
  isEditAttributesEnabled?: boolean;
  isGroupClassNote?: boolean;
  isNoteAuthor: boolean;
  isSessionListEnabled?: boolean;
  noteId: string;
  noteContentEditorRef: RefObject<EditorV2State>;
  onAttendeeSelection: (userId: string, attended: boolean) => void;
  onEditAttendeesClicked: (isClicked: boolean) => void;
  onFormChange: () => void;
  patientId: string;
  selectedAttendees: AttendeeItem[];
  setFieldValue: (
    key: keyof EditNoteFormData,
    value:
      | boolean
      | string
      | Date
      | NoteMultiSelectOption
      | Array<NoteMultiSelectOption>
      | FireflyTertiaryButtonMenuItem,
  ) => void;
  setUploadedFiles: Dispatch<SetStateAction<AttachmentType[]>>;
  updateFieldErrorReducer: (param: {
    dateError?: string | null;
    titleError?: string | null;
    typeError?: string | null;
  }) => void;
  uploadedFiles: AttachmentType[];
  values: EditNoteFormData;
}

const TOP_LEVEL_ID = 'add-notes-form';

const EditNoteForm = ({
  noteTemplateRevisionFormRef,
  disableField,
  fieldErrorReducer,
  hasEventEnded,
  isAddendumModal,
  isEditAttendeesClicked,
  isEditAttributesEnabled,
  isGroupClassNote,
  isNoteAuthor,
  isSessionListEnabled = true,
  noteId,
  noteContentEditorRef,
  onAttendeeSelection,
  onEditAttendeesClicked,
  onFormChange,
  patientId,
  selectedAttendees,
  setFieldValue,
  setUploadedFiles,
  updateFieldErrorReducer,
  uploadedFiles,
  values,
}: EditNoteFormProps) => {
  const noteValues = values;
  const { initialValues } = useFormikContext<EditNoteFormData>();
  const addendumEditorRef = useRef<EditorV2State | null>(null);

  // EditorV2 reads initialContent only on mount. Formik reinitialises the form when the
  // note loads, so reset the addendum editor with it: the editor then shows what gets saved.
  useEffect(() => {
    addendumEditorRef.current?.commands.setContent(
      initialValues.addendumContent ?? '',
      { emitUpdate: false },
    );
  }, [initialValues]);

  const [noteValueProcessing, setNoteValueProcessing] = useState<boolean>(true);
  const [isTemplateSwitching, setIsTemplateSwitching] =
    useState<boolean>(false);
  const [clickedTemplateId, setClickedTemplateId] = useState<string>(null);

  const patientApptsCurrentDate = useMemo(
    () =>
      values?.activityDate
        ? DateTime.fromJSDate(values.activityDate)
        : DateTime.now(),
    [values?.activityDate],
  );
  const {
    data: patientAppointmentsData,
    error: patientAppointmentsError,
    isLoading: isPatientAppointmentsLoading,
  } = useGetAppointmentDetailsQuery(
    {
      patientId,
      startDateISO: patientApptsCurrentDate.minus({ days: 7 }).toISO(),
      endDateISO: patientApptsCurrentDate.plus({ days: 7 }).toISO(),
      statuses: [AppointmentStatus.COMPLETED, AppointmentStatus.SCHEDULED],
      pageSize: 100,
    },
    { skip: !patientId || !isSessionListEnabled || !values },
  );

  const {
    data: patientAppointmentDetailsByIdData,
    isLoading: isGetPatientAppointmentDetailsByIdLoading,
  } = useGetAppointmentDetailsByIdQuery(noteValues?.appointment?.node, {
    skip: !noteValues?.appointment?.node,
  });

  const { data: adminNoteActivityWithNames } =
    useGetAdminNoteActivityWithNamesEnumQuery();

  const { noteTemplateRevisions, isRevisionDataLoading, selectedRevision } =
    useLatestNoteTemplateRevisionsWithLegacyHandling(
      values.noteTemplateRevision?.node,
    );

  useEffect(() => {
    if (
      !isGetPatientAppointmentDetailsByIdLoading &&
      patientAppointmentDetailsByIdData
    ) {
      const apptValue = util.convertAppointmentDataToButtonMenuItem(
        patientAppointmentDetailsByIdData,
      );
      noteValues.appointment = apptValue;
      setFieldValue('appointment', apptValue);
    }
  }, [isGetPatientAppointmentDetailsByIdLoading]);

  useEffect(() => {
    onFormChange();
  }, [uploadedFiles]);

  const activityWithReasons = useMemo<NoteMultiSelectOption[]>(
    () =>
      (adminNoteActivityWithNames ?? []).map((item) => ({
        code: item?.code,
        label: item.activityWithName,
        value: item.activityWithName,
      })),
    [adminNoteActivityWithNames],
  );

  const { sessionDetailsListOptions } = useNoteFormSessionListOptions({
    patientAppointments: patientAppointmentsData,
    selectedAppointmentDetails: patientAppointmentDetailsByIdData,
    selectedAppointmentId: values?.appointment?.node,
  });

  // Finding a match with note.appointmentExternalId in appointments
  // and update the menu option value object
  useEffect(() => {
    if (
      !isEmpty(sessionDetailsListOptions) &&
      sessionDetailsListOptions.length > 1 &&
      noteValues
    ) {
      const appointmentValue: FireflyTertiaryButtonMenuItem = get(
        noteValues,
        'appointment',
        null,
      );
      if (appointmentValue) {
        const matchedAppointment = sessionDetailsListOptions.find(
          (appointmentDetail) =>
            appointmentDetail.node === appointmentValue.node,
        );
        if (matchedAppointment) {
          noteValues.appointment = matchedAppointment;
          setFieldValue('appointment', matchedAppointment);
        } else if (patientAppointmentDetailsByIdData) {
          const apptMenuItem = util.convertAppointmentDataToButtonMenuItem(
            patientAppointmentDetailsByIdData,
          );
          noteValues.appointment = apptMenuItem;
          setFieldValue('appointment', apptMenuItem);
        }
      }
    }
  }, [
    sessionDetailsListOptions,
    noteValues,
    patientAppointmentDetailsByIdData,
  ]);

  useEffect(() => {
    if (
      activityWithReasons &&
      noteValues?.appointment &&
      patientAppointmentsData
    ) {
      // Fill the other fields with appointment details
      const appointmentDetails = patientAppointmentsData?.data?.find(
        (e) => e.appointmentExternalId === noteValues.appointment.node,
      );

      if (appointmentDetails && noteValues?.activity.length == 0) {
        noteValues.activity = fillActivityWith(
          appointmentDetails,
          activityWithReasons,
        );
        setNoteValueProcessing(false);
      } else {
        setNoteValueProcessing(false);
      }
    }
    if (isEmpty(noteValues?.appointment)) {
      setNoteValueProcessing(false);
    }
    if (isGroupClassNote && values?.attendees) {
      noteValues.activity = getGroupClassActivityWith(
        activityWithReasons,
        values.attendees,
      );
      setNoteValueProcessing(false);
    }
  }, [
    noteValues?.attendees,
    values?.attendees,
    activityWithReasons,
    patientAppointmentsData,
  ]);

  return (
    <Form
      onChange={onFormChange}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      {(isEditAttributesEnabled || !isAddendumModal) && (
        <>
          <EditNoteFormTitleRow
            activityWithOptions={activityWithReasons}
            clickedTemplateId={clickedTemplateId}
            disableField={disableField}
            fieldErrorReducer={fieldErrorReducer}
            formData={noteValues}
            id={TOP_LEVEL_ID}
            isAddendumModal={isAddendumModal}
            isGroupClassNote={isGroupClassNote}
            isPatientAppointmentsLoading={isPatientAppointmentsLoading}
            isRevisionDataLoading={isRevisionDataLoading}
            isSessionListEnabled={isSessionListEnabled}
            noteTemplateRevisions={noteTemplateRevisions}
            onFormChange={onFormChange}
            patientAppointmentsData={patientAppointmentsData}
            patientAppointmentsError={
              isBaseQueryError(patientAppointmentsError)
                ? patientAppointmentsError.data?.toString()
                : undefined
            }
            sessionDetailsList={sessionDetailsListOptions}
            setClickedTemplateId={setClickedTemplateId}
            setFieldValue={setFieldValue}
            setIsTemplateSwitching={setIsTemplateSwitching}
            updateFieldErrorReducer={updateFieldErrorReducer}
          />
          <EditNoteFormAttributesRow
            activityWithOptions={activityWithReasons}
            disableField={disableField}
            fieldErrorReducer={fieldErrorReducer}
            formData={noteValues}
            id={TOP_LEVEL_ID}
            isAddendumModal={isAddendumModal}
            isGroupClassNote={isGroupClassNote}
            isNoteAuthor={isNoteAuthor}
            noteValueProcessing={noteValueProcessing}
            onFormChange={onFormChange}
            onUploadSuccess={(docs: AttachmentType[]) => {
              setUploadedFiles((prev: AttachmentType[]) => [...prev, ...docs]);
            }}
            patientId={patientId}
            setFieldValue={setFieldValue}
            updateFieldErrorReducer={updateFieldErrorReducer}
          />
          {isGroupClassNote && (
            <EditNoteFormAttendeeList
              attendees={values?.attendees}
              declinedUsers={values?.declinedUsers}
              hasEventEnded={hasEventEnded}
              isEditAttendeesClicked={isEditAttendeesClicked}
              onAttendeeSelection={onAttendeeSelection}
              onEditAttendeesClicked={onEditAttendeesClicked}
              selectedAttendees={selectedAttendees}
            />
          )}
        </>
      )}

      {isAddendumModal && (
        <ChakraFormControl
          id={`${TOP_LEVEL_ID}__addendum-editor`}
          isInvalid={Boolean(fieldErrorReducer.editorError)}
        >
          <EditorV2
            containerTestId={`${TOP_LEVEL_ID}__addendum-editor-editor__editor-parent`}
            initialContent={noteValues.addendumContent}
            isInvalid={Boolean(fieldErrorReducer.editorError)}
            onChange={(addendumContent: string) => {
              setFieldValue('addendumContent', addendumContent);
              onFormChange();
            }}
            stateRef={addendumEditorRef}
          />
          <ChakraFormErrorMessage
            fontSize="12px"
            lineHeight="140%"
            marginLeft="8px"
          >
            {fieldErrorReducer.editorError}
          </ChakraFormErrorMessage>
        </ChakraFormControl>
      )}

      {!isAddendumModal &&
        (!noteValues?.noteTemplateRevision ||
          noteValues.noteTemplateRevision?.node ===
            NO_NOTE_TEMPLATE_OPTION.node) && (
          <EditorV2
            isInvalid={Boolean(fieldErrorReducer.editorError)}
            onChange={() => onFormChange()}
            readOnly={disableField}
            stateRef={noteContentEditorRef}
          />
        )}

      {!isAddendumModal &&
        noteValues &&
        noteValues.noteTemplateRevision?.node !==
          NO_NOTE_TEMPLATE_OPTION.node && (
          <NoteTemplateRevisionForm
            noteId={noteId}
            noteTemplateRevisionData={values.noteTemplateRevisionData}
            noteTemplateRevisionSchema={selectedRevision?.templateJson}
            ref={noteTemplateRevisionFormRef}
          />
        )}

      <NoteTemplateSwitchModal
        clickedTemplateId={clickedTemplateId}
        closeModal={() => setIsTemplateSwitching(false)}
        isOpen={isTemplateSwitching}
        isRevisionDataLoading={isRevisionDataLoading}
        noteTemplateRevisions={noteTemplateRevisions}
        onCancel={() => {
          setIsTemplateSwitching(false);
          setClickedTemplateId(noteValues.noteTemplateRevision?.node);
        }}
        patientId={patientId}
        setClickedTemplateId={setClickedTemplateId}
        setFieldValue={setFieldValue}
      />
      <ChakraFlex flexFlow="row wrap" gap="12px" marginTop="12px">
        {uploadedFiles?.map((attachment: AttachmentType) => (
          <NoteAttachmentCard
            document={attachment}
            isAddendumModal={isAddendumModal}
            isNoteAuthor={isNoteAuthor}
            key={`attachment-${attachment.fileName}`}
            patientId={patientId}
            setUploadedFiles={setUploadedFiles}
          />
        ))}
      </ChakraFlex>
    </Form>
  );
};

export default EditNoteForm;
